import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import "../../styles/NotificationAlertsPage.css";

const emptyAdminRule = (taskKey) => ({
  task_key: taskKey,
  notify_days: [],
  notify_roles: [],
  notify_groups: [],
  notify_users: [],
  notify_exclude_users: [],
  escalation_days: [],
  escalation_roles: [],
  escalation_groups: [],
  escalation_users: [],
  escalation_exclude_users: [],
});

const parseArray = (value) => {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeDays = (values, max = 5) =>
  [...new Set(values.map(Number).filter((value) => Number.isInteger(value) && value >= -3650 && value <= 3650))].slice(0, max);

const toOptions = (rows) =>
  (rows || []).map((row) => ({
    value: Number(row.id),
    label: row.email ? `${row.name} (${row.email})${Number(row.status) !== 1 ? " — inactive" : ""}` : row.name,
  }));

const MultiPicker = ({ options, value, onChange, placeholder }) => {
  const selected = options.filter((option) => value.includes(option.value));
  return (
    <Select
      isMulti
      classNamePrefix="notification-select"
      options={options}
      value={selected}
      onChange={(items) => onChange((items || []).map((item) => item.value))}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      menuPlacement="bottom"
    />
  );
};

const DayInputs = ({ value, onChange, escalation = false }) => {
  const slots = [value[0] ?? "", value[1] ?? ""];
  return (
    <div className="notification-day-inputs">
      {slots.map((day, index) => (
        <label key={index}>
          <span>Number of Days</span>
          <input
            className="notification-day-value"
            type="text"
            inputMode="numeric"
            value={day}
            placeholder={escalation ? (index ? "2" : "7") : (index ? "15" : "30")}
            onChange={(event) => {
              if (!/^-?\d*$/.test(event.target.value)) return;
              const next = [...slots];
              next[index] = event.target.value;
              onChange(next.filter((item) => item !== ""));
            }}
          />
        </label>
      ))}
    </div>
  );
};

const RecipientFields = ({ prefix, rule, setRule, options }) => (
  <div className="notification-recipient-fields">
    {[
      ["roles", "Select Role(s)", options.roles],
      ["groups", "Select Group(s)", options.groups],
      ["users", "Select User(s)", options.admins],
      ["exclude_users", "Exclude User(s)", options.admins],
    ].map(([key, label, choices]) => (
      <div className="notification-field-row" key={key}>
        <label>{label}</label>
        <MultiPicker
          options={choices}
          value={rule[`${prefix}_${key}`]}
          onChange={(value) => setRule({ ...rule, [`${prefix}_${key}`]: value })}
          placeholder={label}
        />
      </div>
    ))}
  </div>
);

const CollapsibleCard = ({ title, description, open, onToggle, children }) => (
  <section className={`notification-card notification-collapsible-card ${open ? "is-open" : "is-closed"}`}>
    <button type="button" className="notification-collapse-heading" onClick={onToggle} aria-expanded={open}>
      <span><h3>{title}</h3><p>{description}</p></span>
      <i className={`fa-solid fa-chevron-${open ? "down" : "right"}`} aria-hidden="true" />
    </button>
    {open && <div className="notification-collapsible-content">{children}</div>}
  </section>
);

const AdminRuleList = ({ items, rules, updateRule, options, type }) => (
  <div className="admin-document-list">
    {items.map((item, index) => {
      const rule = rules[index];
      const isTraining = type === "training";
      if (!rule) return null;
      const expiryPeriods = isTraining ? item.expiry_period_months || [] : [];
      return <article className="admin-document-rule" key={isTraining ? item.training_key : item.task_key}>
        <h4><span>{isTraining ? "Training" : "Document Type"}</span>{isTraining ? item.training_name : item.document_type}{isTraining && <small>{expiryPeriods.length ? `${expiryPeriods.join(" / ")} month expiry` : "No expiry period"}</small>}</h4>
        <div className="admin-rule-columns">
          <div><h5>Notify</h5><DayInputs value={rule.notify_days} onChange={(value) => updateRule(index, { ...rule, notify_days: value })} /><RecipientFields prefix="notify" rule={rule} setRule={(next) => updateRule(index, next)} options={options} /></div>
          <div><h5>Notification Escalation</h5><DayInputs escalation value={rule.escalation_days} onChange={(value) => updateRule(index, { ...rule, escalation_days: value })} /><RecipientFields prefix="escalation" rule={rule} setRule={(next) => updateRule(index, next)} options={options} /></div>
        </div>
      </article>;
    })}
  </div>
);

const NotificationAlertsPage = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    [],
  );
  const [activeTab, setActiveTab] = useState("candidate");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [candidateDays, setCandidateDays] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [associateRules, setAssociateRules] = useState([]);
  const [statusRules, setStatusRules] = useState([]);
  const [adminRules, setAdminRules] = useState([]);
  const [trainingRules, setTrainingRules] = useState([]);
  const [adminSections, setAdminSections] = useState({ status: false, documents: false, training: false });
  const [options, setOptions] = useState({ statuses: [], roles: [], groups: [], admins: [], users: [] });

  const loadConfiguration = useCallback(async () => {
    try {
      setLoading(true);
      setMessage("");
      const { data } = await axios.get(`${BASE_URL}/api/notifications/configuration`, { headers: authHeaders });
      const associateMap = new Map(
        (data.associate || []).map((rule) => [
          rule.task_key,
          { task_key: rule.task_key, enabled: Boolean(Number(rule.enabled)), days: parseArray(rule.days).map(Number) },
        ]),
      );
      const adminMap = new Map(
        (data.admin_rules || []).map((rule) => [
          rule.dnr_task_key,
          {
            task_key: rule.dnr_task_key,
            notify_days: parseArray(rule.dnr_notify_days).map(Number),
            notify_roles: parseArray(rule.dnr_notify_roles).map(Number),
            notify_groups: parseArray(rule.dnr_notify_groups).map(Number),
            notify_users: parseArray(rule.dnr_notify_users).map(Number),
            notify_exclude_users: parseArray(rule.dnr_notify_exclude_users).map(Number),
            escalation_days: parseArray(rule.dnr_escalation_days).map(Number),
            escalation_roles: parseArray(rule.dnr_escalation_roles).map(Number),
            escalation_groups: parseArray(rule.dnr_escalation_groups).map(Number),
            escalation_users: parseArray(rule.dnr_escalation_users).map(Number),
            escalation_exclude_users: parseArray(rule.dnr_escalation_exclude_users).map(Number),
          },
        ]),
      );
      const trainingMap = new Map((data.training_rules || []).map((rule) => [rule.tnr_training_key, {
        task_key: rule.tnr_training_key,
        notify_days: parseArray(rule.tnr_notify_days).map(Number), notify_roles: parseArray(rule.tnr_notify_roles).map(Number), notify_groups: parseArray(rule.tnr_notify_groups).map(Number), notify_users: parseArray(rule.tnr_notify_users).map(Number), notify_exclude_users: parseArray(rule.tnr_notify_exclude_users).map(Number),
        escalation_days: parseArray(rule.tnr_escalation_days).map(Number), escalation_roles: parseArray(rule.tnr_escalation_roles).map(Number), escalation_groups: parseArray(rule.tnr_escalation_groups).map(Number), escalation_users: parseArray(rule.tnr_escalation_users).map(Number), escalation_exclude_users: parseArray(rule.tnr_escalation_exclude_users).map(Number),
      }]));
      setCandidateDays(Number(data.candidate?.candidate_delay_days || 0));
      setDocuments(data.documents || []);
      setTrainings(data.trainings || []);
      setAssociateRules((data.documents || []).map((doc) => associateMap.get(doc.task_key) || { task_key: doc.task_key, enabled: false, days: [] }));
      setStatusRules((data.status_rules || []).map((rule) => ({
        id: rule.id,
        from_status: Number(rule.from_status),
        to_status: Number(rule.to_status),
        recipient_user_ids: parseArray(rule.recipient_user_ids).map(Number),
      })));
      setAdminRules((data.documents || []).map((doc) => adminMap.get(doc.task_key) || emptyAdminRule(doc.task_key)));
      setTrainingRules((data.trainings || []).map((training) => trainingMap.get(training.training_key) || emptyAdminRule(training.training_key)));
      setOptions({
        statuses: toOptions(data.options?.statuses),
        roles: toOptions(data.options?.roles),
        groups: toOptions(data.options?.groups),
        admins: toOptions(data.options?.admins),
        users: toOptions(data.options?.users),
      });
    } catch (error) {
      console.error("Notification settings load failed:", error);
      setMessage(error.response?.data?.message || "Failed to load notification settings.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, authHeaders]);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const updateAssociateRule = (index, next) =>
    setAssociateRules((rules) => rules.map((rule, ruleIndex) => ruleIndex === index ? next : rule));

  const updateAdminRule = (index, next) =>
    setAdminRules((rules) => rules.map((rule, ruleIndex) => ruleIndex === index ? next : rule));

  const updateTrainingRule = (index, next) => setTrainingRules((rules) => rules.map((rule, ruleIndex) => ruleIndex === index ? next : rule));
  const toggleAdminSection = (section) => setAdminSections((sections) => ({ ...sections, [section]: !sections[section] }));

  const updateStatusRuleFrom = (index, fromStatus) => {
    const nextFrom = Number(fromStatus);
    setStatusRules((rules) => rules.map((rule, ruleIndex) => {
      if (ruleIndex !== index) return rule;
      const currentTo = Number(rule.to_status);
      const nextTo = currentTo === nextFrom
        ? options.statuses.find((option) => option.value !== nextFrom)?.value || ""
        : currentTo;
      return { ...rule, from_status: nextFrom, to_status: nextTo };
    }));
  };

  const save = async () => {
    try {
      setSaving(true);
      setMessage("");
      if (activeTab === "candidate") {
        await axios.put(`${BASE_URL}/api/notifications/candidate`, { candidate_delay_days: Number(candidateDays) }, { headers: authHeaders });
      } else if (activeTab === "associate") {
        await axios.put(`${BASE_URL}/api/notifications/associate`, { rules: associateRules }, { headers: authHeaders });
      } else {
        const transitions = statusRules.map((rule) => `${Number(rule.from_status)}:${Number(rule.to_status)}`);
        if (statusRules.some((rule) => !rule.from_status || !rule.to_status || Number(rule.from_status) === Number(rule.to_status))) {
          setMessage("Each status rule must have two different statuses.");
          return;
        }
        if (new Set(transitions).size !== transitions.length) {
          setMessage("Each status transition can only be configured once.");
          return;
        }
        await axios.put(
          `${BASE_URL}/api/notifications/administrator`,
          { status_rules: statusRules, document_rules: adminRules, training_rules: trainingRules },
          { headers: authHeaders },
        );
      }
      setMessage("Notification settings saved.");
      await loadConfiguration();
    } catch (error) {
      console.error("Notification settings save failed:", error);
      setMessage(error.response?.data?.message || "Failed to save notification settings.");
    } finally {
      setSaving(false);
    }
  };

  const statusName = (id) => options.statuses.find((option) => option.value === Number(id))?.label || "";

  return (
    <div className="form-wrapper my-2 notification-alerts-page account-settings-tab-panel account-settings-notifications-tab">
      <div className="notification-page-header account-settings-tab-header">
        <div>
          <h2>Notifications / Alerts</h2>
          <p>Configure automatic candidate, associate, and administrator email notifications.</p>
        </div>
        <button className="btn btn-primary notification-save-button" onClick={save} disabled={loading || saving}>
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      <div className="nav nav-tabs notification-tabs" role="tablist">
        {["candidate", "associate", "administrator"].map((tab) => (
          <button key={tab} className={`nav-link ${activeTab === tab ? "active" : ""}`} onClick={() => { setActiveTab(tab); setMessage(""); }}>
            {tab[0].toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {message && <div className={`notification-message ${message.includes("Failed") ? "error" : ""}`}>{message}</div>}
      {loading ? <div className="notification-loading">Loading notification settings…</div> : (
        <>
          {activeTab === "candidate" && (
            <section className="notification-card candidate-notification-card">
              <div>
                <h3>Incomplete Application Notification</h3>
                <p>Choose how many days the system should wait before sending one email prompt to a candidate whose application is still incomplete. Enter 0 to disable this feature.</p>
              </div>
              <label>
                <span>Wait (days)</span>
                <input type="number" min="0" max="3650" value={candidateDays} onChange={(event) => setCandidateDays(event.target.value)} />
              </label>
            </section>
          )}

          {activeTab === "associate" && (
            <section className="notification-card">
              <h3>Document Expiration / Renewal Notifications</h3>
              <p>Select Email and one or more reminder intervals for each document. Custom values may be positive (before expiry) or negative (after expiry).</p>
              <div className="associate-rule-table">
                <div className="associate-rule-header">
                  <span>Document Type</span><span>Email</span><span>30 Days</span><span>60 Days</span><span>90 Days</span><span>Custom #1</span><span>Custom #2</span>
                </div>
                {documents.map((doc, index) => {
                  const rule = associateRules[index];
                  const custom = rule.days.filter((day) => ![30, 60, 90].includes(day));
                  return (
                    <div className="associate-rule-row" key={doc.task_key}>
                      <strong>{doc.document_type}</strong>
                      <input type="checkbox" checked={rule.enabled} onChange={(event) => updateAssociateRule(index, { ...rule, enabled: event.target.checked })} aria-label={`Email ${doc.document_type}`} />
                      {[30, 60, 90].map((day) => (
                        <input key={day} type="checkbox" checked={rule.days.includes(day)} onChange={(event) => {
                          const days = event.target.checked ? [...rule.days, day] : rule.days.filter((item) => item !== day);
                          updateAssociateRule(index, { ...rule, days: normalizeDays(days) });
                        }} aria-label={`${day} days for ${doc.document_type}`} />
                      ))}
                      {[0, 1].map((slot) => (
                        <input className="notification-day-value" key={slot} type="text" inputMode="numeric" value={custom[slot] ?? ""} onChange={(event) => {
                          if (!/^-?\d*$/.test(event.target.value)) return;
                          const customs = [...custom];
                          customs[slot] = event.target.value;
                          const standards = rule.days.filter((day) => [30, 60, 90].includes(day));
                          updateAssociateRule(index, { ...rule, days: [...standards, ...customs.filter((item) => item !== "")] });
                        }} aria-label={`Custom ${slot + 1} for ${doc.document_type}`} />
                      ))}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {activeTab === "administrator" && (
            <>
              <CollapsibleCard title="Status Notifications" description="Send an email to selected administrators whenever a configured status transition occurs. Status emails include the associate's Org Unit." open={adminSections.status} onToggle={() => toggleAdminSection("status")}>
                <div className="notification-section-actions">
                  <button type="button" className="btn btn-outline-primary notification-secondary-button" onClick={() => setStatusRules((rules) => [...rules, {
                    id: `new-${Date.now()}`,
                    from_status: options.statuses[0]?.value || "",
                    to_status: options.statuses[1]?.value || "",
                    recipient_user_ids: [],
                  }])}><i className="fa-solid fa-plus" aria-hidden="true" /> Add rule</button>
                </div>
                <div className="status-rule-list">
                  {statusRules.length === 0 && <div className="notification-empty status-rule-empty"><strong>No status notification rules yet</strong><span>Add a rule to notify administrators when a person's status changes.</span></div>}
                  {statusRules.map((rule, index) => (
                    <div className="status-rule-row" key={rule.id || index}>
                      <label><span>From status</span><select value={rule.from_status} onChange={(event) => updateStatusRuleFrom(index, event.target.value)}>
                        {options.statuses.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                      </select></label>
                      <span className="status-transition-arrow" aria-hidden="true"><i className="fa-solid fa-arrow-right" /></span>
                      <label><span>To status</span><select value={rule.to_status} onChange={(event) => setStatusRules((rules) => rules.map((item, i) => i === index ? { ...item, to_status: Number(event.target.value) } : item))}>
                        {options.statuses.filter((option) => option.value !== Number(rule.from_status)).map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                      </select></label>
                      <label className="status-recipient-field"><span>Notification recipients</span><MultiPicker options={options.admins} value={rule.recipient_user_ids} onChange={(value) => setStatusRules((rules) => rules.map((item, i) => i === index ? { ...item, recipient_user_ids: value } : item))} placeholder="Select administrator(s)" /><small>{rule.recipient_user_ids.length ? `${rule.recipient_user_ids.length} administrator${rule.recipient_user_ids.length === 1 ? "" : "s"} selected` : "Choose who should receive this notification"}</small></label>
                      <button type="button" className="notification-delete-button" onClick={() => setStatusRules((rules) => rules.filter((_, i) => i !== index))} aria-label={`Delete ${statusName(rule.from_status)} to ${statusName(rule.to_status)} rule`}><i className="fa-solid fa-trash-can" aria-hidden="true" /></button>
                    </div>
                  ))}
                </div>
              </CollapsibleCard>

              <CollapsibleCard title="Document Expiration / Renewal Notifications" description="Configure administrator reminders for document expiry and renewal. Digest emails are separated into Org Unit groups." open={adminSections.documents} onToggle={() => toggleAdminSection("documents")}>
                <p>Positive numbers create ranges before expiry. For example, Notify values 30 and 15 send once during days 30–16 and again during days 15–0; Escalation values 7 and 2 separately notify escalation recipients during days 7–3 and 2–0. Each recipient receives one combined email containing each newly eligible document once. Only active associates are included in the document list. Recipients are all active users in the selected roles or groups, plus directly selected users, minus excluded users.</p>
                <div className="admin-document-list">
                  {documents.map((doc, index) => {
                    const rule = adminRules[index];
                    return (
                      <article className="admin-document-rule" key={doc.task_key}>
                        <h4><span>Document Type</span>{doc.document_type}</h4>
                        <div className="admin-rule-columns">
                          <div>
                            <h5>Notify</h5>
                            <DayInputs value={rule.notify_days} onChange={(value) => updateAdminRule(index, { ...rule, notify_days: value })} />
                            <RecipientFields prefix="notify" rule={rule} setRule={(next) => updateAdminRule(index, next)} options={options} />
                          </div>
                          <div>
                            <h5>Notification Escalation</h5>
                            <DayInputs escalation value={rule.escalation_days} onChange={(value) => updateAdminRule(index, { ...rule, escalation_days: value })} />
                            <RecipientFields prefix="escalation" rule={rule} setRule={(next) => updateAdminRule(index, next)} options={options} />
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </CollapsibleCard>

              <CollapsibleCard title="Training Expiration / Redo Notifications" description="Configure administrator reminders for expiring training. Digest emails are separated into Org Unit groups." open={adminSections.training} onToggle={() => toggleAdminSection("training")}>
                <p>Training modules with the same course name are grouped into one setting. TDG Chapters One through Seven are grouped together, and Part 1 / Part 2 courses are grouped by their shared name. Only active associates with a recorded training expiry date are included.</p>
                <AdminRuleList items={trainings} rules={trainingRules} updateRule={updateTrainingRule} options={options} type="training" />
              </CollapsibleCard>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationAlertsPage;
